/**
 * Richard Quinn — theme JS
 *
 * - Drawers (menu, cart) with overlay + body scroll lock
 * - Header: mobile submenu accordion, search panel
 * - Cart: AJAX add / change / remove via the Cart API + Section Rendering API
 *
 * Exposed as window.theme.cart so other templates can call
 * theme.cart.add(new FormData(form)) or theme.cart.open().
 */
(function () {
  'use strict';

  var theme = (window.theme = window.theme || {});
  var routes = theme.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  var mobile = window.matchMedia('(max-width: 768px)');
  var nav = window.matchMedia('(max-width: 1360px)');
  var openDrawers = [];

  /* ------------------------------------------------------------------------
     Drawers
     ------------------------------------------------------------------------ */

  function overlay() {
    return document.querySelector('[data-overlay]');
  }

  function setExpanded(el, open) {
    if (!el.id) return;
    document.querySelectorAll('[aria-controls="' + el.id + '"]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', String(open));
    });
  }

  function openDrawer(el) {
    if (!el || el.classList.contains('is-open')) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    setExpanded(el, true);
    openDrawers.push(el);
    document.body.classList.add('drawer-open');
    if (overlay()) overlay().classList.add('is-visible');

    var focusTarget = el.querySelector('[data-drawer-focus]') || el.querySelector('button, a, input, select');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  function closeDrawer(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    setExpanded(el, false);
    openDrawers = openDrawers.filter(function (drawer) { return drawer !== el; });
    if (!openDrawers.length) {
      document.body.classList.remove('drawer-open');
      if (overlay()) overlay().classList.remove('is-visible');
    }
  }

  function closeAll() {
    openDrawers.slice().forEach(closeDrawer);
    closeSearch();
    closeAppointment();
  }

  /* ------------------------------------------------------------------------
     Header: search panel
     ------------------------------------------------------------------------ */

  function header() {
    return document.querySelector('[data-header]');
  }

  function toggleSearch(force) {
    var el = header();
    if (!el) return;
    var open = typeof force === 'boolean' ? force : !el.classList.contains('header--search-open');
    el.classList.toggle('header--search-open', open);
    el.querySelectorAll('[data-search-toggle]').forEach(function (button) {
      button.setAttribute('aria-expanded', String(open));
    });
    if (open) {
      var input = el.querySelector('[data-search-input]');
      if (input) input.focus();
    }
  }

  function closeSearch() {
    toggleSearch(false);
  }

  /* ------------------------------------------------------------------------
     Category nav — the collections share one hero image, so moving between
     them looks like nothing happened. A category link flags the next load,
     which then jumps past the hero so the header sits on top of it. Flagged
     rather than done by URL hash, so a shared or direct link still opens on
     the hero.
     ------------------------------------------------------------------------ */

  var SKIP_HERO = 'skip-hero';

  function flagSkipHero() {
    try {
      sessionStorage.setItem(SKIP_HERO, '1');
    } catch (error) {
      /* private mode: the hero simply does not get skipped */
    }
  }

  function skipHero() {
    var flagged;
    try {
      flagged = sessionStorage.getItem(SKIP_HERO) === '1';
      if (flagged) sessionStorage.removeItem(SKIP_HERO);
    } catch (error) {
      return;
    }
    if (!flagged) return;

    var content = document.querySelector('[data-hero-content]');
    if (!content) return;
    var header = document.querySelector('[data-header]');
    var offset = header ? header.offsetHeight : 0;
    var top = content.getBoundingClientRect().top + window.scrollY - offset;
    if (top > 0) window.scrollTo({ top: top, behavior: 'instant' });
  }

  /* ------------------------------------------------------------------------
     Footer menus — collapsed accordions on mobile, always open above the
     breakpoint. Driven by the open attribute rather than CSS, because
     browsers that hide details content via ::details-content ignore a
     display override on the children. Without JS they stay open, which is
     the usable fallback.
     ------------------------------------------------------------------------ */

  function syncFooterMenus() {
    document.querySelectorAll('[data-footer-menu]').forEach(function (el) {
      el.open = !mobile.matches;
    });
  }

  mobile.addEventListener('change', syncFooterMenus);

  /* ------------------------------------------------------------------------
     Appointment bubble — the fixed trigger reveals its links
     ------------------------------------------------------------------------ */

  function appointment() {
    return document.querySelector('[data-appointment]');
  }

  function setAppointment(open) {
    var el = appointment();
    if (!el) return;
    el.classList.toggle('is-open', open);
    el.querySelectorAll('[data-appointment-toggle]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', String(open));
    });
  }

  function toggleAppointment() {
    var el = appointment();
    setAppointment(!!el && !el.classList.contains('is-open'));
  }

  function closeAppointment() {
    setAppointment(false);
  }

  /* ------------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------------ */

  var cart = {
    drawer: function () {
      return document.querySelector('[data-cart-drawer]');
    },

    sectionId: function () {
      var drawer = this.drawer();
      return (drawer && drawer.dataset.sectionId) || 'cart-drawer';
    },

    open: function () {
      openDrawer(this.drawer());
    },

    close: function () {
      closeDrawer(this.drawer());
    },

    /** Swap the drawer contents with freshly rendered section HTML and update the header count. */
    render: function (html, count) {
      var drawer = this.drawer();
      if (drawer && html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-inner]');
        var inner = drawer.querySelector('[data-cart-inner]');
        if (fresh && inner) inner.innerHTML = fresh.innerHTML;
      }
      if (typeof count === 'number') {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
      }
    },

    request: function (url, options) {
      var self = this;
      var drawer = this.drawer();
      if (drawer) drawer.classList.add('is-loading');

      return fetch(url, options)
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (data) {
          self.render(data.sections && data.sections[self.sectionId()], data.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
          return data;
        })
        .catch(function (error) {
          document.dispatchEvent(new CustomEvent('cart:error', { detail: error }));
          throw error;
        })
        .finally(function () {
          if (drawer) drawer.classList.remove('is-loading');
        });
    },

    /** Change the quantity of a line (1-based index). Quantity 0 removes it. */
    change: function (line, quantity) {
      return this.request(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.sectionId(),
          sections_url: window.location.pathname
        })
      });
    },

    /** Add to cart from a product form's FormData (or a plain { id, quantity } object), then open the drawer. */
    add: function (formData) {
      var self = this;
      var body = formData instanceof FormData ? formData : new FormData();
      if (!(formData instanceof FormData) && formData) {
        Object.keys(formData).forEach(function (key) { body.append(key, formData[key]); });
      }
      body.append('sections', this.sectionId());
      body.append('sections_url', window.location.pathname);

      return this.request(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      }).then(function (data) {
        self.open();
        return data;
      });
    },

    /** Re-render the drawer from the server (for changes made elsewhere). */
    refresh: function () {
      var self = this;
      var id = this.sectionId();
      return Promise.all([
        fetch(routes.root + '?sections=' + id, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
      ]).then(function (results) {
        self.render(results[0][id], results[1].item_count);
      });
    }
  };

  theme.cart = cart;
  theme.openDrawer = openDrawer;
  theme.closeDrawer = closeDrawer;
  theme.closeAll = closeAll;

  /* ------------------------------------------------------------------------
     Events (delegated so section re-renders need no re-binding)
     ------------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var target = event.target;
    var trigger;

    if (target.closest('[data-menu-open]')) {
      event.preventDefault();
      openDrawer(document.querySelector('[data-menu]'));
      return;
    }
    if (target.closest('[data-menu-close]')) {
      closeDrawer(document.querySelector('[data-menu]'));
      return;
    }
    if (target.closest('[data-cart-open]')) {
      event.preventDefault();
      cart.open();
      return;
    }
    if (target.closest('[data-cart-close]')) {
      cart.close();
      return;
    }
    if (target.closest('[data-search-toggle]')) {
      event.preventDefault();
      toggleSearch();
      return;
    }
    if (target.closest('[data-search-close]')) {
      closeSearch();
      return;
    }
    if ((trigger = target.closest('[data-zoom-in], [data-zoom-out]'))) {
      var gallery = trigger.closest('[data-gallery]');
      var swiper = gallery && gallery.swiper;
      if (swiper && swiper.zoom) {
        if (trigger.hasAttribute('data-zoom-in')) swiper.zoom.in(); else swiper.zoom.out();
      }
      return;
    }
    if (target.closest('[data-skip-hero]')) flagSkipHero();

    if (target.closest('[data-appointment-toggle]')) {
      event.preventDefault();
      toggleAppointment();
      return;
    }
    if (target.closest('[data-overlay]')) {
      closeAll();
      return;
    }
    if ((trigger = target.closest('[data-cart-remove]'))) {
      event.preventDefault();
      cart.change(Number(trigger.dataset.line), 0);
      return;
    }

    // Mobile: tapping a top-level item with children toggles its submenu instead of navigating
    var link = target.closest('.header__item.has-children > .header__link');
    if (link && nav.matches) {
      event.preventDefault();
      var item = link.parentElement;
      var open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      link.setAttribute('aria-expanded', String(open));
      return;
    }

    // Clicking outside the appointment bubble closes its links
    if (!target.closest('[data-appointment]')) closeAppointment();

    // Clicking outside the search panel closes it
    var el = header();
    if (el && el.classList.contains('header--search-open') && !target.closest('[data-search], [data-search-toggle]')) {
      closeSearch();
    }
  });

  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-cart-qty]');
    if (select) cart.change(Number(select.dataset.line), Number(select.value));
  });

  // Opt-in AJAX add-to-cart for any <form data-ajax-cart> (product template can use this later)
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();
    var button = form.querySelector('[data-add-to-cart]');
    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }
    cart.add(new FormData(form)).catch(function (error) {
      console.warn('[theme] add to cart failed', error);
    }).then(function () {
      if (!button) return;
      button.classList.remove('is-loading');
      button.disabled = false;
      updateProductForm(form);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });

  // Leaving the nav breakpoint with the menu drawer open would leave the nav stuck open
  nav.addEventListener('change', function () {
    closeDrawer(document.querySelector('[data-menu]'));
  });

  // Theme editor: open the cart drawer when its section is selected
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-cart-drawer]')) cart.open();
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target.querySelector('[data-cart-drawer]')) cart.close();
  });

  /* ------------------------------------------------------------------------
     Product gallery zoom — magnify on hover above the breakpoint; below it
     the gallery is a Swiper, so pinch, double-tap and the zoom buttons are
     Swiper's own and this does nothing
     ------------------------------------------------------------------------ */

  function zoomOrigin(item, event) {
    var img = item.querySelector('img');
    if (!img) return;
    var box = item.getBoundingClientRect();
    img.style.transformOrigin =
      ((event.clientX - box.left) / box.width) * 100 + '% ' +
      ((event.clientY - box.top) / box.height) * 100 + '%';
  }

  document.addEventListener('mouseover', function (event) {
    if (mobile.matches) return;
    var item = event.target.closest('[data-zoom]');
    if (!item) return;
    item.classList.add('is-zoomed');
    zoomOrigin(item, event);
  });

  document.addEventListener('mousemove', function (event) {
    if (mobile.matches) return;
    var item = event.target.closest('[data-zoom]');
    if (item) zoomOrigin(item, event);
  });

  // The origin is deliberately left where the cursor was: resetting it here
  // would snap to centre while the scale is still easing back, which reads
  // as a jump. The next mouseover sets a fresh origin before scaling up.
  document.addEventListener('mouseout', function (event) {
    var item = event.target.closest('[data-zoom]');
    if (!item || item.contains(event.relatedTarget)) return;
    item.classList.remove('is-zoomed');
  });

  /* ------------------------------------------------------------------------
     Carousels — any element with data-swiper='{ …Swiper config JSON… }'
     - pagination / navigation selectors are resolved inside the element
     - "spaceBetween": "css" reads the --swiper-gap CSS variable (so gaps
       live in style.css and change at the breakpoint)
     ------------------------------------------------------------------------ */

  function swiperGap(el) {
    var value = parseFloat(getComputedStyle(el).getPropertyValue('--swiper-gap'));
    return isNaN(value) ? 0 : value;
  }

  function createSwiper(el, json) {
    if (el.swiper) return;
    var config;
    try {
      config = JSON.parse(json || '{}');
    } catch (error) {
      console.warn('[theme] invalid swiper config', el, error);
      return;
    }

    ['pagination', 'navigation', 'scrollbar'].forEach(function (key) {
      if (!config[key]) return;
      ['el', 'nextEl', 'prevEl'].forEach(function (prop) {
        if (typeof config[key][prop] === 'string') config[key][prop] = el.querySelector(config[key][prop]);
      });
    });

    if (config.spaceBetween === 'css') {
      config.spaceBetween = swiperGap(el);
      config.on = Object.assign({}, config.on, {
        resize: function (swiper) {
          var gap = swiperGap(el);
          if (gap === swiper.params.spaceBetween) return;
          swiper.params.spaceBetween = gap;
          swiper.originalParams.spaceBetween = gap;
          swiper.update();
        }
      });
    }

    new window.Swiper(el, config);
  }

  function initSwipers(root) {
    if (typeof window.Swiper !== 'function') return;
    (root || document).querySelectorAll('[data-swiper]').forEach(function (el) {
      createSwiper(el, el.dataset.swiper);
    });
    initMobileSwipers(root);
  }

  // [data-swiper-mobile]: a Swiper only at the mobile breakpoint (e.g. the product gallery), plain markup on desktop
  function initMobileSwipers(root) {
    if (typeof window.Swiper !== 'function') return;
    (root || document).querySelectorAll('[data-swiper-mobile]').forEach(function (el) {
      if (mobile.matches) {
        createSwiper(el, el.dataset.swiperMobile);
      } else if (el.swiper) {
        el.swiper.destroy(true, true);
      }
    });
  }
  mobile.addEventListener('change', function () { initMobileSwipers(document); });

  function initVideos(root) {
    (root || document).querySelectorAll('video[autoplay]').forEach(function (video) {
      video.setAttribute('playsinline', '');
      video.muted = true;
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    });
  }

  theme.initSwipers = initSwipers;

  /* ------------------------------------------------------------------------
     Collection / search: filters, sort and "Load more" through the Section
     Rendering API. [data-grid] (with data-section-id) is the part of
     the section that gets swapped; the facets <form> submits normally without JS.
     ------------------------------------------------------------------------ */

  var facetsPushed = false;

  function renderGrid(container, url, options) {
    var fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', container.dataset.sectionId);
    container.classList.add('is-loading');

    return fetch(fetchUrl.toString())
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-grid]');
        if (!fresh) return;
        if (options && options.append) {
          var items = container.querySelector('[data-grid-items]');
          var freshItems = fresh.querySelector('[data-grid-items]');
          var footer = container.querySelector('[data-grid-footer]');
          var freshFooter = fresh.querySelector('[data-grid-footer]');
          if (items && freshItems) Array.prototype.slice.call(freshItems.children).forEach(function (item) { items.appendChild(item); });
          if (footer) { if (freshFooter) footer.replaceWith(freshFooter); else footer.remove(); }
        } else {
          container.innerHTML = fresh.innerHTML;
        }
        initSwipers(container);
        initVideos(container);
      })
      .finally(function () { container.classList.remove('is-loading'); });
  }

  function facetsUrl(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) { if (value !== '') params.append(key, value); });
    var query = params.toString();
    return form.getAttribute('action') + (query ? '?' + query : '');
  }

  document.addEventListener('change', function (event) {
    var form = event.target.closest('form[data-facets]');
    var container = form && form.closest('[data-grid]');
    if (!container) return;
    var url = facetsUrl(form);
    history.pushState({ facets: true }, '', url);
    facetsPushed = true;
    renderGrid(container, url);
  });

  document.addEventListener('click', function (event) {
    var more = event.target.closest('[data-load-more]');
    if (more) {
      var moreContainer = more.closest('[data-grid]');
      if (!moreContainer) return;
      event.preventDefault();
      more.setAttribute('aria-busy', 'true');
      renderGrid(moreContainer, more.href, { append: true });
      return;
    }

    var clear = event.target.closest('[data-facets-clear]');
    if (clear) {
      var clearContainer = clear.closest('[data-grid]');
      if (!clearContainer) return;
      event.preventDefault();
      history.pushState({ facets: true }, '', clear.href);
      facetsPushed = true;
      renderGrid(clearContainer, clear.href);
      return;
    }

    // Close open filter dropdowns when clicking elsewhere
    if (!event.target.closest('details[data-facet]')) {
      document.querySelectorAll('details[data-facet][open]').forEach(function (details) { details.removeAttribute('open'); });
    }
  });

  // Only one filter dropdown open at a time ('toggle' does not bubble, so listen in the capture phase)
  document.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details.matches || !details.matches('details[data-facet]') || !details.open) return;
    document.querySelectorAll('details[data-facet][open]').forEach(function (other) {
      if (other !== details) other.removeAttribute('open');
    });
  }, true);

  window.addEventListener('popstate', function (event) {
    var container = document.querySelector('[data-grid]');
    if (container && (facetsPushed || (event.state && event.state.facets))) {
      renderGrid(container, window.location.href);
    }
  });

  /* ------------------------------------------------------------------------
     Product page: the variant picker — chips drive the hidden variant id,
     the price, the button state and the URL
     ------------------------------------------------------------------------ */

  function formatMoney(cents) {
    var format = theme.moneyFormat || '{{amount}}';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1] === '00') parts.pop();
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, parts.join('.'));
  }

  function selectedOptions(form) {
    return Array.prototype.map.call(form.querySelectorAll('.product-form__option'), function (fieldset) {
      var checked = fieldset.querySelector('input[data-option-value]:checked');
      return checked ? checked.value : null;
    });
  }

  function updateProductForm(form) {
    var fieldsets = form.querySelectorAll('.product-form__option');
    if (!fieldsets.length) return;
    var variants;
    try {
      variants = JSON.parse(form.querySelector('[data-variants]').textContent);
    } catch (error) {
      return;
    }
    var strings = theme.strings || {};
    var options = selectedOptions(form);
    var matches = function (variant, wanted) {
      return variant.options.every(function (value, index) { return wanted[index] === null || value === wanted[index]; });
    };
    var variant = variants.find(function (candidate) { return matches(candidate, options); });

    var idInput = form.querySelector('[data-variant-id]');
    var button = form.querySelector('[data-add-to-cart]');
    var text = form.querySelector('[data-add-to-cart-text]');
    var price = document.querySelector('[data-product-price]');
    var sku = form.querySelector('[data-variant-sku]');

    if (variant) {
      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (sku) {
        sku.hidden = !variant.sku;
        sku.querySelector('[data-variant-sku-value]').textContent = variant.sku || '';
      }
      if (button) button.disabled = !variant.available;
      if (text) text.textContent = variant.available ? strings.addToBag : strings.soldOut;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url.toString());
    } else {
      if (button) button.disabled = true;
      if (text) text.textContent = strings.unavailable;
    }

    var soldOutEnquire = document.querySelector('[data-sold-out-enquire]');
    if (soldOutEnquire) soldOutEnquire.hidden = !!(variant && variant.available);

    // Grey out values that cannot be combined with the other selected options
    fieldsets.forEach(function (fieldset, index) {
      fieldset.querySelectorAll('input[data-option-value]').forEach(function (input) {
        var wanted = options.slice();
        wanted[index] = input.value;
        var possible = variants.some(function (candidate) { return candidate.available && matches(candidate, wanted); });
        var label = form.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('is-unavailable', !possible);
      });
    });
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches('input[data-option-value]')) return;
    var form = event.target.closest('form[data-product-form]');
    if (form) updateProductForm(form);
  });

  /* ------------------------------------------------------------------------
     Product recommendations — fetched after load from the section rendered by
     the recommendations endpoint, then swapped in.
     ------------------------------------------------------------------------ */

  function loadRecommendations(root) {
    (root || document).querySelectorAll('[data-recommendations]').forEach(function (el) {
      if (el.dataset.loaded || !el.dataset.url) return;
      el.dataset.loaded = 'true';
      fetch(el.dataset.url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
          } else {
            el.hidden = true;
          }
        })
        .catch(function () { el.hidden = true; });
    });
  }

  /* ------------------------------------------------------------------------
     Modals — native <dialog>, so focus trapping, Escape and the backdrop are
     the browser's job. [data-modal-open="id"] opens #modal-id.
     ------------------------------------------------------------------------ */

  var modalOpener = null;

  function openModal(modal, trigger) {
    if (!modal || typeof modal.showModal !== 'function' || modal.open) return false;
    modalOpener = trigger || null;
    modal.showModal();
    return true;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-modal-open]');
    if (opener) {
      // No modal on the page (e.g. no form block): leave the trigger's href alone
      var modal = document.getElementById('modal-' + opener.dataset.modalOpen);
      if (!modal || typeof modal.showModal !== 'function') return;
      event.preventDefault();
      openModal(modal, opener);
      return;
    }
    if (event.target.closest('[data-modal-close]')) {
      var open = event.target.closest('dialog.modal');
      if (open) open.close();
      return;
    }
    // A backdrop click targets the dialog itself; its content sits in .modal__dialog
    if (event.target.matches && event.target.matches('dialog.modal')) event.target.close();
  });

  // role="button" triggers need Space, which links don't activate natively
  document.addEventListener('keydown', function (event) {
    if (event.key !== ' ') return;
    var trigger = event.target.closest && event.target.closest('[data-modal-open][role="button"]');
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  });

  document.addEventListener('close', function (event) {
    if (!event.target.matches || !event.target.matches('dialog.modal')) return;
    if (modalOpener) modalOpener.focus();
    modalOpener = null;
  }, true);

  /* ------------------------------------------------------------------------
     Enquiry: open the shared modal from any [data-enquire="<name>"] trigger
     and write that name into the Shopify Forms product field.

     The app renders into an OPEN SHADOW ROOT on <shopify-forms-embed>,
     asynchronously, and re-renders discard a plain value assignment — so the
     value goes through the native setter, the events are composed so they
     cross the shadow boundary, and each root is watched plus polled.
     Ported from the SCP theme, with the value now set per trigger because one
     modal serves a whole grid of looks.
     ------------------------------------------------------------------------ */

  var enquiry = {
    value: '',
    timer: null,
    observed: (typeof WeakSet === 'function') ? new WeakSet() : null,

    fieldId: function () {
      var holder = document.querySelector('#modal-enquiry [data-field-id]');
      return (holder && holder.dataset.fieldId) || 'custom#product';
    },

    /** Write the current value into one shadow root's product field. */
    fill: function (root) {
      var el = root.querySelector('[id="' + this.fieldId() + '"]');
      if (!el || el.value === this.value) return;
      var proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement : HTMLInputElement;
      Object.getOwnPropertyDescriptor(proto.prototype, 'value').set.call(el, this.value);
      el.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
      el.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    },

    fillAll: function () {
      var self = this;
      document.querySelectorAll('shopify-forms-embed').forEach(function (host) {
        var root = host.shadowRoot;
        if (!root) return;
        if (self.observed && !self.observed.has(host)) {
          self.observed.add(host);
          new MutationObserver(function () { self.fill(root); }).observe(root, { childList: true, subtree: true });
        }
        self.fill(root);
      });
    },

    /** Set the value and keep re-applying it while the app hydrates or re-renders. */
    set: function (value) {
      var self = this;
      this.value = value || '';

      if (this.timer) clearInterval(this.timer);
      if (!this.value) return;

      this.fillAll();
      var giveUp = Date.now() + 20000;
      this.timer = setInterval(function () {
        self.fillAll();
        if (Date.now() > giveUp) {
          clearInterval(self.timer);
          self.timer = null;
        }
      }, 300);
    }
  };

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-enquire]');
    if (!trigger) return;
    var modal = document.getElementById('modal-enquiry');
    // Without a modal on the page the trigger keeps its href (e.g. an enquiry page)
    if (!modal || typeof modal.showModal !== 'function') return;
    event.preventDefault();
    enquiry.set(trigger.getAttribute('data-enquire'));
    openModal(modal, trigger);
  });

  /* ------------------------------------------------------------------------
     Initialisation — every load-time and theme-editor hook in one place
     ------------------------------------------------------------------------ */

  function init(root) {
    skipHero();
    syncFooterMenus();
    initSwipers(root);
    initVideos(root);
    root.querySelectorAll('form[data-product-form]').forEach(updateProductForm);
    loadRecommendations(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
    // A standalone enquiry page can pass the subject as ?product=
    var fromUrl = new URLSearchParams(window.location.search).get('product');
    if (fromUrl) enquiry.set(fromUrl);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Theme editor: selecting a slide block shows that slide
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest('.swiper-slide');
    var swiperEl = slide && slide.closest('.swiper');
    var swiper = swiperEl && swiperEl.swiper;
    if (!swiper) return;
    var index = Array.prototype.indexOf.call(slide.parentElement.children, slide);
    if (swiper.params.loop) swiper.slideToLoop(index); else swiper.slideTo(index);
    if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
  });
})();
